<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcCompanyFormContracts',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    company: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref([]);
  const b_1468474219929533_displayCounterparty = computed(() => false);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_1468474219929533_displayCounterparty:
      b_1468474219929533_displayCounterparty.value,
  }));

  const $$load_items = () => {
    return $fn.value
      .fetch('list_contract', {
        parameters: { filter: 'counterparty_id eq "' + props.company.id + '"' },
      })
      .then((resp) =>
        _.orderBy(resp.data, ['valid_from', 'valid_to'], ['desc', 'desc'])
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      {
        name: 'b_1468474219929533_displayCounterparty',
        nodeId: '1468474219929533',
      },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux, b_1468474219929533_displayCounterparty },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.company,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('company');
    }
  );

  const inst_evh_8320016629450276_close_mmbo4zdx6_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_8320016629450276_showModal_n5sy2jhml_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateContract',
      props: {
        clientId: props.company.id,
      },
    };
  };

  const act_8320016629450276_reloadComponentData_g3fm2es4z =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8320016629450276_close_mmbo4zdx6 =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_g3fm2es4z,
      event: 'close',
      when: inst_evh_8320016629450276_close_mmbo4zdx6_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_8320016629450276_showModal_n5sy2jhml =
    new actionTypes.ShowModalAction({
      actionArgs: act_8320016629450276_showModal_n5sy2jhml_getActionArgs,
      events: [inst_evh_8320016629450276_close_mmbo4zdx6],
    });
  const inst_evh_8320016629450276_clickPreventStop_2o31q1mfv =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_showModal_n5sy2jhml,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_2o31q1mfv],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_8320016629450276_close_mmbo4zdx6,
    inst_evh_8320016629450276_clickPreventStop_2o31q1mfv,
    inst_evh_8320016629450276_clickPreventStop,
  };

  const evh_8320016629450276_close_mmbo4zdx6 = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_close_mmbo4zdx6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_2o31q1mfv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_2o31q1mfv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpWidgetCard>
    <template #header>
      <ItpBox alignItems="center">
        <strong>
          {{ pgettext('Company', 'Contracts') }}
        </strong>
        <ItpButton
          :text="gettext('Create contract...')"
          size="sm"
          variant="light"
          class="ms-auto"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </template>
    <BListGroup v-if="loaded">
      <BListGroupItem class="card-body text-center" v-if="!items.length">
        <span>
          {{ pgettext('Company', 'No related contracts.') }}
        </span>
      </BListGroupItem>
      <BListGroupItem
        :key="item.id"
        class="d-flex flex-column"
        v-for="item in items"
      >
        <UcContractCard
          :item="item"
          :displayCounterparty="b_1468474219929533_displayCounterparty"
        >
        </UcContractCard>
      </BListGroupItem>
    </BListGroup>
  </ItpWidgetCard>
</template>
